/**
 * Step 2 of the configure screen: one compact row per variable —
 * `{{report}} maps to Input › report`. The pencil swaps in a path box (typed
 * freely, with suggestions); the chevron shows the value this sample has. The
 * row last picked is the one a click in the sample's JSON tree fills. A field
 * the sample has no value for says "Not in this sample".
 */

import { useEffect, useRef, useState } from "react";
import { ChevronRight, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { valueAt } from "@/lib/json";
import type { MappingRow } from "../mapping-rows";
import { PathInput } from "../path-input";
import type { PathInfo } from "../types";
import { crumbs } from "../variables";

const absent = (v: unknown) => v === undefined || v === null;
const shown = (v: unknown) => (typeof v === "string" ? v : JSON.stringify(v, null, 2));

interface RowsProps {
  rows: MappingRow[];
  value: Record<string, string>;
  paths: PathInfo[];
  sample?: Record<string, unknown> | undefined;
  focused: string | null;
  onFocus: (name: string) => void;
  onChange: (name: string, path: string) => void;
}

function Row({
  row,
  path,
  paths,
  sample,
  focused,
  onFocus,
  onChange,
}: Omit<RowsProps, "rows" | "value" | "focused" | "onChange"> & {
  row: MappingRow;
  path: string;
  focused: boolean;
  onChange: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const token = `{{${row.name}}}`;
  const empty = !path.trim();
  // An unmapped row needs a path, so its box stays out.
  const typing = editing || empty;
  useEffect(() => {
    if (editing) box.current?.querySelector("input")?.focus();
  }, [editing]);
  const value = sample && !empty ? valueAt(sample, path.trim()) : undefined;
  const missing = !!sample && !empty && absent(value);
  return (
    <li
      onClick={() => onFocus(row.name)}
      className={`rounded-md border ${focused ? "border-focus bg-selected" : "border-line"}`}
    >
      <div className="flex min-h-10 items-center gap-2 py-1 pr-1 pl-1.5 text-xs">
        <Button
          size="sm"
          variant="ghost"
          icon
          aria-expanded={open}
          aria-label={`Show the value of ${token}`}
          onClick={() => setOpen(!open)}
        >
          <ChevronRight
            aria-hidden
            className={`size-3.5 motion-safe:transition-transform ${open ? "rotate-90" : ""}`}
          />
        </Button>
        {/* Wraps on a phone, so the field never shrinks to nothing. */}
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex shrink-0 flex-col">
            <code className="font-mono font-semibold text-link">{token}</code>
            {(row.label || !row.required) && (
              <span className="text-faint">
                {row.label}
                {!row.required && `${row.label ? " · " : ""}optional`}
              </span>
            )}
          </span>
          <span className="shrink-0 text-muted">maps to</span>
          {typing ? (
            <span ref={box} className="min-w-40 flex-1">
              <PathInput
                label={`${token} maps to`}
                value={path}
                paths={paths}
                sample={sample}
                invalid={row.required && empty}
                onFocus={() => onFocus(row.name)}
                onChange={onChange}
              />
            </span>
          ) : (
            <span className="max-w-full min-w-0 truncate font-semibold text-ink" title={path}>
              {crumbs(path).join(" › ")}
            </span>
          )}
          {missing && !typing && (
            <span className="flex shrink-0 items-center gap-1.5 text-warn">
              <span aria-hidden className="size-1.5 rounded-full bg-warn" />
              Not in this sample
            </span>
          )}
        </span>
        {!empty && (
          <Button
            size="sm"
            variant="ghost"
            icon
            aria-pressed={editing}
            aria-label={`Change the field for ${token}`}
            onClick={() => setEditing(!editing)}
          >
            <Pencil aria-hidden className="size-3.5" />
          </Button>
        )}
      </div>
      {open && (
        <pre className="m-0 max-h-40 overflow-y-auto border-t border-line-soft px-3 py-2 font-mono text-xs break-words whitespace-pre-wrap text-body">
          {!sample
            ? "Pick a sample on the right to see its value."
            : empty
              ? "Not mapped yet."
              : missing
                ? "No value in this sample."
                : shown(value)}
        </pre>
      )}
    </li>
  );
}

export function VariableRows({ rows, value, ...rest }: RowsProps) {
  if (rows.length === 0)
    return (
      <p className="m-0 rounded-md bg-well px-3 py-2.5 text-xs text-muted">
        No variables yet. Add one to the prompt, like {"{{output}}"}.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label="Variable mappings" className="m-0 flex list-none flex-col gap-1.5 p-0">
        {rows.map((r) => (
          <Row
            key={r.name}
            row={r}
            path={value[r.name] ?? ""}
            {...rest}
            focused={rest.focused === r.name}
            onChange={(p) => rest.onChange(r.name, p)}
          />
        ))}
      </ul>
      <span className="text-xs text-faint">
        Pick a row, then click a field in the sample data to fill it.
      </span>
    </div>
  );
}

/** The variables this sample has no value for (none until a sample is loaded). */
export function missingIn(
  rows: MappingRow[],
  value: Record<string, string>,
  sample: Record<string, unknown> | undefined,
): string[] {
  return sample
    ? rows
        .filter((r) => value[r.name]?.trim() && absent(valueAt(sample, value[r.name]!.trim())))
        .map((r) => r.name)
    : [];
}
